import { useState, useEffect } from 'react';
import { Alert } from 'antd';
import {
  Drawer,
  Row,
  message,
  Tag,
  Button,
  Col,
  Steps,
  Card,
  Typography,
  Input,
  Select,
} from 'antd';
import { Form } from '@ant-design/compatible';
import moment from 'moment';
import Map from '../../../assets/images/map.svg';

import './DetalhePedido.scss';
import ResumoDetalhePedido from './ResumoDetalhePedido';
import VendedorDetalhePedido from './VendedorDetalhePedido';
import ProdutoValoresDetalhePedido from './ProdutoValoresDetalhePedido';
import MapaPedido from './MapaPedido';
import DashedValueRow from '../../../shared/components/dashed-value-row/DashedValueRow';

import {
  accept as acceptService,
  reject,
  finish as finishService,
  cancel,
  getOrigem,
  getStatusColor,
  getStatusText,
  setDriver,
} from '../../../shared/services/http/PedidoService';
import ConfirmDelete from '../../../shared/components/confirmdelete/ConfirmDelete';
import {
  isBackOfficeOrUpgasEmployee,
  isResale,
} from '../../../shared/services/UserService';
import OrderStatusEnum from '../../../shared/Enum/OrderStatusEnum';
import TypeSalesmanEnum from '../../../shared/Enum/TypeSalesmanEnum';
import ConfirmVerificationCode from './ConfirmVerificationCode';
import RadioGroup from '../../../shared/components/inputs/radiogroup/RadioGroup';
import MotivoCancelamentoRevendaEnum from '../../../shared/Enum/MotivoCancelamentoRevendaEnum';
import { inputs } from './detalhePedidoInputs';
import { withAppInsightsTracking } from '../../../shared/services/AppInsightsService';
import { find } from '../../../shared/services/http/PedidoService';
import DetalhePedidoSkeleton from './DetalhePedidoSkeleton';
import ReactPlaceholder from 'react-placeholder';
import { get } from '../../../shared/services/http/MotoristaService';
import MotoristaDetalhePedido from './MotoristaDetalhePedido';
import { ExclamationCircleOutlined } from '@ant-design/icons';

const { Step } = Steps;

const deliveryTimeOptions = [
  { value: '15 à 30 min', text: '15 à 30 min' },
  { value: '30 à 45 min', text: '30 à 45 min' },
  { value: '45 à 60 min', text: '45 à 60 min' },
  { value: '60 à 90 min', text: '60 à 90 min' },
];

const resaleCancelOptions = [
  {
    value: MotivoCancelamentoRevendaEnum.VamosFechar,
    text: 'Vamos fechar',
  },
  {
    value: MotivoCancelamentoRevendaEnum.ProblemasComVeiculos,
    text: 'Problemas com veículos',
  },
  {
    value: MotivoCancelamentoRevendaEnum.ForaDeArea,
    text: 'Fora de área, muito longe',
  },
  {
    value: MotivoCancelamentoRevendaEnum.AreaDeRisco,
    text: 'Área de risco',
  },
  {
    value: MotivoCancelamentoRevendaEnum.ClienteSolicitouCancelamento,
    text: 'Cliente solicitou o cancelamento',
  },
  {
    value: MotivoCancelamentoRevendaEnum.PedidoFoiTrote,
    text: 'O pedido foi trote',
  },
  {
    value: MotivoCancelamentoRevendaEnum.TelefoneClienteInvalido,
    text: 'Telefone do cliente inválido',
  },
  {
    value: MotivoCancelamentoRevendaEnum.EnderecoClienteInvalido,
    text: 'Endereço do cliente inválido',
  },
  {
    value: MotivoCancelamentoRevendaEnum.Outro,
    text: 'Outro motivo',
  },
];

const getStatusDescription = (h) =>
  h.status === OrderStatusEnum.AceitoPelaOrganizacao ||
  h.status === OrderStatusEnum.AceitoPeloMotorista ||
  h.status === OrderStatusEnum.AceitoPeloAdm ||
  h.status === OrderStatusEnum.AceitoPeloFuncionarioUpgas ? (
      <small style={{ color: '#515151' }}>
        Entrega até:
        {moment(h.limiteEntrega).format('HH:mm')}{' '}
      </small>
    ) : (
      <small
        style={{
          color: '#515151',
        }}>
        {moment(h.data).format('DD/MM/YYYY HH:mm')}
      </small>
    );

export const isAccepted = (statusId) =>
  statusId === OrderStatusEnum.AceitoPeloMotorista ||
  statusId === OrderStatusEnum.AceitoPelaOrganizacao ||
  statusId === OrderStatusEnum.AceitoPeloAdm ||
  statusId === OrderStatusEnum.AceitoPeloFuncionarioUpgas;

const rejectedAndCanceledStatus = [
  OrderStatusEnum.RejeitadoPeloAdm,
  OrderStatusEnum.RejeitadoPeloFuncionarioUpgas,
  OrderStatusEnum.RejeitadoPelaOrganizacao,
  OrderStatusEnum.RejeitadoPeloMotorista,
  OrderStatusEnum.CanceladoPeloAdm,
  OrderStatusEnum.CanceladoPeloFuncionarioUpgas,
  OrderStatusEnum.CanceladoPelaOrganizacao,
  OrderStatusEnum.CanceladoPeloCliente,
];

const zoomMap = 11;

const BACKOFFICE_STATUSES = [
  OrderStatusEnum.AceitoPeloFuncionarioUpgas,
  OrderStatusEnum.FinalizadoPeloFuncionarioUpgas,
  OrderStatusEnum.AceitoPeloAdm,
  OrderStatusEnum.FinalizadoPeloAdm,
  OrderStatusEnum.CanceladoPeloAdm,
  OrderStatusEnum.CanceladoPeloFuncionarioUpgas,
  OrderStatusEnum.RejeitadoPeloAdm,
  OrderStatusEnum.RejeitadoPeloFuncionarioUpgas,
];

/**
 * Formats the status title with or without the backoffice user name
 *
 * @param {string} status - Status from history
 * @param {string} backofficeUserName - Backoffice user name
 * @returns {string} Formatted title
 */
const formatStatusTitle = (status, backofficeUserName) => {
  const baseTitle = getStatusText(status);
  const shouldShowUserName = backofficeUserName && BACKOFFICE_STATUSES.includes(status);

  return shouldShowUserName
    ? `${baseTitle} (${backofficeUserName})`
    : baseTitle;
};

/**
 * Represents a component for displaying detailed information about a pedido (order).
 *
 * @param {Object} props - The props object containing the following properties:
 *   - isOpen: A boolean indicating whether the drawer is open or not.
 *   - orderId: The ID of the order.
 *   - form: The form object for form validation.
 *   - onClose: A function to be called when the drawer is closed.
 * @returns {JSX.Element} The JSX element representing the DetalhePedido component.
 */
function DetalhePedido({ isOpen, orderId, form, onClose }) {
  const [order, setOrder] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingError, setLoadingError] = useState(false);
  const [rejectingOrCanceling, setRejectingOrCanceling] = useState(false);
  const [selectedCancelOption, setSelectedCancelOption] = useState(null);
  const [reasonForCancellationOthers, setReasonForCancellationOthers] =
    useState('');
  const [showTheMap, setShowTheMap] = useState(false);
  const [drawerWidth, setDrawerWidth] = useState(700);
  const [driverOptions, setDriversOptions] = useState([]);
  const [driverId, setDriverId] = useState()


  const toggleLoading = (callBack) => {
    setLoading((prevState) => !prevState);
    callBack && callBack();
  };

  const toggleErrorLoading = (callBack) => {
    callBack && callBack();
  };

  useEffect(() => {
    if (isOpen) {
      toggleLoading();
      find(orderId)
        .then((order) => setOrder(order))
        .catch((error) => message.error(error))
        .finally(() => toggleLoading());
    }

    get(1, 50, null, order.resaleId).promise
      .then(result => {
        const mappedDrivers = result.items.map(driver => ({
          value: driver.id,
          label: driver.nome
        }));
        setDriversOptions(mappedDrivers);
      })
      .catch(error => console.error('Error fetching drivers:', error));

    const updateWidth = () => {
      setDrawerWidth(window.innerWidth <= 800 ? window.innerWidth : 800);
    };
    window.addEventListener('resize', updateWidth);
    updateWidth();
    return () => window.removeEventListener('resize', updateWidth);
  }, [isOpen, orderId, order.resaleId]);

  const beforeClose = () => {
    setRejectingOrCanceling(false);
    setSelectedCancelOption(null);
    onClose();
  };

  const accept = async (values, deliveryTime) => {
    try {
      await acceptService(values, deliveryTime);
      toggleLoading(() => {
        beforeClose();
        message.success('Pedido em andamento!');
      });
    } catch (error) {
      toggleLoading(() => message.error(error));
    }
  };

  const orderReject = async () => {
    toggleErrorLoading(async () => {
      try {
        await reject({
          ...order,
          motivoCancelamentoRevenda: selectedCancelOption,
          motivo: reasonForCancellationOthers,
        });
        toggleErrorLoading(() => {
          beforeClose();
          message.success('Pedido rejeitado com sucesso!');
        });
      } catch (error) {
        toggleErrorLoading(() => message.error(error));
      }
    });
  };

  const orderCanceled = async () => {
    toggleErrorLoading(async () => {
      try {
        await cancel({
          ...order,
          motivoCancelamentoRevenda: selectedCancelOption,
          motivo: reasonForCancellationOthers,
        });
        toggleErrorLoading(() => {
          beforeClose();
          message.success('Pedido cancelado com sucesso!');
        });
      } catch (error) {
        toggleErrorLoading(() => message.error(error));
      }
    });
  };

  const finish = async (code = null, withCodeConfirmation) => {
    toggleLoading(async () => {
      try {
        if (
          withCodeConfirmation &&
          order.codigoConfirmacao &&
          order.codigoConfirmacao !== code
        ) {
          toggleLoading(() => message.error('Código de verificação inválido'));
          return;
        }

        await finishService(order, withCodeConfirmation);

        toggleLoading(() => {
          beforeClose();
          message.success('Pedido finalizado com sucesso!');
        });
      } catch (error) {
        toggleLoading(() => message.error(error));
      }
    });
  };

  const selectDriver = async () => {
    try {
      await setDriver(order, driverId)
      message.success('Motorista selecionado com sucesso')
    } catch (error) {
      toggleLoading(() => message.error(error));
    }
  }

  const handleSubmit = (event) => {
    event.preventDefault();

    form.validateFieldsAndScroll((_, { selectedTime }) =>
      toggleLoading(() => accept(order, selectedTime))
    );
  };

  const handleRejectCancel = () => {
    order.statusId === OrderStatusEnum.Solicitado
      ? orderReject()
      : orderCanceled();
  };

  const valorProdutos = order.products && order.products.reduce((acc, curr) => acc + (curr.valor * curr.quantidade), 0)

  return (
    <Drawer
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',  paddingRight: 20 }}>
          <div>
            <Tag>{order.protocol}</Tag>
            <Tag color={order.statusColor}>{order.status}</Tag>
            {isBackOfficeOrUpgasEmployee() && (
              <Tag data-testid="origem-pedido">{getOrigem(order.origem)}</Tag>
            )}
          </div>

          {order.suspectedFraud && (
            <span
              style={{
                border: '1px solid red',
                color: 'red',
                padding: '10px 8px',
                borderRadius: '6px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 14,
                marginRight: -40,
              }}
            >
              <ExclamationCircleOutlined style={{ color: 'red' }} />
              Suspeita de fraude!
            </span>
          )}

          {order.averageDeliveryTime && (
            <div
              style={{
                border: '1px solid #004B8D',
                color: '#004B8D',
                padding: '2px 8px',
                borderRadius: '6px',
                display: 'inline-flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                fontSize: 14,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'start',
                  gap: 6,
                }}
              >
                <span style={{ color: '#595959' }}>Tempo médio de entrega</span>
                <strong style={{ marginTop: 6}}>
                  {`${Math.floor(moment.duration(order.averageDeliveryTime).asHours())}h ${moment
                    .duration(order.averageDeliveryTime)
                    .minutes()}min`}
                </strong>
              </div>
              <span style={{ fontSize: 10, color: '#595959', marginTop: -12, alignItems: 'left' }}>Últimas entregas</span>
            </div>
          )}
        </div>
      }
      width={drawerWidth}
      onClose={beforeClose}
      visible={isOpen}>
      <ReactPlaceholder
        showLoadingAnimation={true}
        ready={!loading}
        customPlaceholder={<DetalhePedidoSkeleton />}>
        {!rejectingOrCanceling ? (
          <>
            {showTheMap && (
              <MapaPedido
                localizacaoVendedor={
                  order.salesMan
                    ? order.salesMan.localizacao
                    : { latitude: 0, longitude: 0 }
                }
                localizacaoCliente={
                  order.customer
                    ? order.customer.localizacao
                    : { latitude: 0, longitude: 0 }
                }
                tipoPedido={order.tipoPedido}
                zoom={zoomMap}
              />
            )}

            <Form
              className='order-detail'
              layout='vertical'
              onSubmit={handleSubmit}>
              <Form.Item>
                {order.statusId === OrderStatusEnum.Solicitado && order.customer?.hasRecentCancellation && (
                  <Alert
                    message="Atenção: Este cliente teve um pedido cancelado recentemente."
                    description="Fique atento ao analisar este pedido."
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                )}

                {order.historicoStatus && (
                  <div className='mb-20'>
                    <Row gutter={14}>
                      <Col>
                        <h2 className='title-detail'>Histórico</h2>
                      </Col>
                    </Row>
                    <Steps>
                      {order.historicoStatus.map((h, index) => (
                        <Step
                          key={index}
                          title={
                            <Tag color={getStatusColor(h.status)}>
                              {formatStatusTitle(h.status, h.backofficeUserName)}
                            </Tag>
                          }
                          icon={<></>}
                          description={getStatusDescription(h)}
                        />
                      ))}
                    </Steps>
                    {rejectedAndCanceledStatus.some(
                      (status) => status === order.statusId
                    ) &&
                      order.cancelReason === 'Outro motivo' && (
                      <Card className='order-cancel-reason-description'>
                        <small>
                          <b>Motivo cancelamento:</b>
                        </small>
                        <Typography.Paragraph className='description'>
                          {order.cancelReasonDescription}
                        </Typography.Paragraph>
                      </Card>
                    )}
                  </div>
                )}

                {order.customer && <ResumoDetalhePedido order={order} />}
                <Button onClick={() => setShowTheMap(true)} type='primary' style={{ marginTop: "1rem"}}>
                  <img
                    style={{ width: 14, height: 14, fill: 'white', marginRight: ".5rem" }}
                    src={Map}
                    alt='icone mapa'
                  />
                  <span>
                    Ver mapa
                  </span>
                </Button>

                <div style={{ display: "flex", flexWrap: "wrap", columnGap: "4rem", rowGap: "1rem", marginTop: "1rem"}}>
                  <ProdutoValoresDetalhePedido order={order} />
                  {order.salesMan && <VendedorDetalhePedido order={order} />}
                  {order.driverId && <MotoristaDetalhePedido order={order} />}
                </div>

                {/* <Typography.Title level={2} style={{ marginTop: '10px'}}>Motorista</Typography.Title>
                <div style={{display: 'flex', gap: '1rem'}}>
                  <Select
                    style={{ width: 300 }}
                    placeholder="Selecionar"
                    options={driverOptions}
                    onChange={value => setDriverId(value)}
                  />
                  <Button
                    type='primary'
                    onClick={() => selectDriver()}
                  >Salvar</Button>
                </div> */}

                <Row className={'mt-20'}>
                  <Col span={24}>
                    <DashedValueRow title='Valor' value={valorProdutos} />
                    <DashedValueRow title='Desconto' value={order.discount} type="negative" />
                    <DashedValueRow
                      title='Taxa do cliente'
                      value={order.taxaServico}
                    />
                    <DashedValueRow
                      title='Cupom'
                      value={order.coupon?.discount ?? order.descontoIndiqueGanhe ?? 'R$ 0,00'}
                      type="negative"
                    />
                    <DashedValueRow
                      title='Valor Total'
                      value={order.priceWithDiscount}
                    />
                  </Col>
                </Row>

                <Row className={'mt-20'}>
                  <Col>
                    <span>Forma de pagamento: </span>
                    <Typography.Text>{ order.paymentType }</Typography.Text>
                  </Col>
                  <Col className='dashed-space' />
                  <Col>
                    <span>Troco: </span>
                    <Typography.Text>
                      { order.change }
                    </Typography.Text>
                  </Col>
                </Row>

                {order.statusId === OrderStatusEnum.Solicitado && (
                  <Row className={'mt-20'}>
                    <Col span={14}>
                      {order.tipoPedido === TypeSalesmanEnum.Resale && (
                        <>
                          <span>Selecione o tempo de entrega</span>
                          <RadioGroup
                            name={inputs.selectedTime.name}
                            rules={[
                              {
                                required: true,
                                message: 'Tempo estimado é obrigatório!',
                              },
                            ]}
                            form={form}
                            options={deliveryTimeOptions}
                          />
                        </>
                      )}
                    </Col>
                    <Col
                      span={10}
                      style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        marginTop: '20px',
                      }}>
                      {(isBackOfficeOrUpgasEmployee() || isResale()) && (
                        <Button
                          onClick={() => setRejectingOrCanceling(true)}
                          className='reject-button reject-order'>
                          Rejeitar
                        </Button>
                      )}
                      <Button
                        onClick={handleSubmit}
                        disabled={
                          order.tipoPedido === TypeSalesmanEnum.Resale &&
                          !form.getFieldValue(inputs.selectedTime.name)
                        }
                        type='primary'>
                        Aceitar
                      </Button>
                    </Col>
                  </Row>
                )}
                {isAccepted(order.statusId) && (
                  <Row
                    gutter={14}
                    className={'mt-20'}
                    type='flex'
                    justify='end'>
                    <Button
                      onClick={() => setRejectingOrCanceling(true)}
                      className='cancel-button reject-order'>
                      Cancelar
                    </Button>
                    <Button
                      loading={loading}
                      onClick={() =>
                        order.codigoConfirmacao
                          ? ConfirmVerificationCode(finish)
                          : ConfirmDelete(
                            finish,
                            'Você realmente deseja finalizar esse pedido?'
                          )
                      }
                      className='finish-button reject-order'>
                      Finalizar
                    </Button>
                  </Row>
                )}
              </Form.Item>
            </Form>
          </>
        ) : (
          <>
            <h1>Motivo de Rejeição/Cancelamento</h1>
            {resaleCancelOptions.map(({ value, text }) => (
              <Button
                className='mb-10'
                block
                onClick={() => setSelectedCancelOption(value)}
                type={value === selectedCancelOption ? 'primary' : 'default'}>
                {text}
              </Button>
            ))}

            {selectedCancelOption === MotivoCancelamentoRevendaEnum.Outro && (
              <Input.TextArea
                rows={4}
                placeholder="Descreva o motivo do cancelamento (Obrigatório)"
                onChange={({ target }) =>
                  setReasonForCancellationOthers(target.value)
                }
              />
            )}

            <Row type='flex' justify='end' className='mt-10'>
              <Button
                className='mr-10'
                onClick={() => setRejectingOrCanceling(false)}>
                Cancelar
              </Button>
              <Button
                type='primary'
                disabled={
                  selectedCancelOption === null ||
                  (selectedCancelOption === MotivoCancelamentoRevendaEnum.Outro &&
                   !reasonForCancellationOthers.trim())
                }
                onClick={() => handleRejectCancel()}>
                Enviar
              </Button>
            </Row>
          </>
        )}
      </ReactPlaceholder>
    </Drawer>
  );
}

export default withAppInsightsTracking(Form.create()(DetalhePedido));
